import React, { useEffect, useState, useCallback, memo } from "react";
import { Row } from "react-bootstrap";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import ReCAPTCHA from "react-google-recaptcha";
import "./SignUp.css";
import instance, { AllCountryList, apiEncryptRequest, decryptData } from "../../Api";
import { useAuth } from "../../AuthContext";
import { useGoogleLogin } from "@react-oauth/google";
import axios from "axios";
import Loader from "../common/Loader";
import toast, { Toaster } from "react-hot-toast";
import AppToast from "../../AppToast";

const RECAPTCHA_KEY = "6Ld0OugrAAAAANg5wUSXM5GiOzVips3QSeR_Jcf4";

const isPWA = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  !!window.navigator.standalone;

const COUNTRY_SELECT_STYLE = {
  width: "100%",
  padding: "10px 15px",
  border: "0px solid white",
  borderRadius: "30px",
  fontSize: "18px",
  color: "white",
  backgroundColor: "transparent",
  appearance: "none",
  backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='18' height='18'><path fill='%23ffffff' d='M7 10l5 5 5-5z'/></svg>")`,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 15px center",
  backgroundSize: "16px",
  outline: "none",
};

// Responsive CSS injected once — avoids all fixed-width inline style issues
const MOBILE_CSS = `
  .su-card {
    box-sizing: border-box;
    width: 92%;
    max-width: 480px;
    margin: 9rem auto 2rem;
    padding: 28px 20px;
    background: #07528b;
    border-radius: 16px;
    background-image: url("/images/blue-box-bg.svg");
    background-size: cover;
  }
  .su-field {
    display: flex;
    align-items: center;
    background: rgba(255,255,255,0.12);
    border-radius: 30px;
    padding: 4px 14px;
    margin-bottom: 14px;
    gap: 8px;
    min-height: 48px;
    box-sizing: border-box;
  }
  .su-field img { width:20px; height:20px; flex-shrink:0; object-fit:contain; }
  .su-input {
    flex: 1;
    min-width: 0;
    background: transparent;
    border: none;
    outline: none;
    color: white;
    font-size: 16px;
    padding: 8px 4px;
  }
  .su-input::placeholder { color: rgba(255,255,255,0.6); }
  .su-google-btn {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    background: white;
    color: #444;
    border: none;
    border-radius: 8px;
    padding: 12px 20px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    margin-bottom: 14px;
  }
  .su-google-btn img { width:20px; height:20px; }
  .su-or {
    text-align: center;
    color: rgba(255,255,255,0.75);
    font-size: 14px;
    margin: 0 0 14px;
  }
  .su-note {
    font-size: 13px;
    font-weight: 600;
    color: white;
    text-align: center;
    margin: 4px 0 14px;
  }
  .su-captcha {
    display: flex;
    justify-content: center;
    margin: 0 0 16px;
    overflow: hidden;
  }
  /* Scale down reCAPTCHA on very narrow screens */
  @media (max-width: 360px) {
    .su-captcha > div { transform: scale(0.88); transform-origin: center; }
  }
  @media (max-width: 300px) {
    .su-captcha > div { transform: scale(0.72); transform-origin: center; }
  }
  .su-submit {
    width: 100%;
    padding: 13px;
    background: white;
    color: #07528B;
    font-size: 17px;
    font-weight: 700;
    border: none;
    border-radius: 30px;
    cursor: pointer;
    margin-bottom: 16px;
    letter-spacing: 0.3px;
    transition: opacity 0.15s;
  }
  .su-submit:active { opacity: 0.8; }
  .su-already {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    color: rgba(255,255,255,0.85);
    flex-wrap: wrap;
  }
  .su-already a { color:white; font-weight:700; text-decoration:underline; }
`;

// ── Sub-components ────────────────────────────────────────────────────────────
const EyeToggle = memo(({ show, onToggle }) =>
  show
    ? <FaEyeSlash onClick={onToggle} style={{ color:"white", width:"20px", flexShrink:0, cursor:"pointer" }} />
    : <FaEye     onClick={onToggle} style={{ color:"white", width:"20px", flexShrink:0, cursor:"pointer" }} />
);

const CountrySelect = memo(({ onChange }) => (
  <select style={COUNTRY_SELECT_STYLE} onChange={(e) => onChange(e.target.value)} defaultValue="">
    <option value="" disabled hidden>Select Country</option>
    {AllCountryList?.map((item) => (
      <option key={item.value} value={item.name} style={{ color:"black" }}>{item.name}</option>
    ))}
  </select>
));

// Web-only Google button (original style unchanged)
const GoogleButtonWeb = memo(({ onClick }) => (
  <button onClick={onClick} style={{ marginLeft:"15px", justifyContent:"center", display:"flex", alignItems:"center", backgroundColor:"white", color:"black", border:"none", borderRadius:"4px", padding:"10px 20px", fontSize:"18px", cursor:"pointer" }}>
    <img src="/images/google_logo_icon.svg" alt="Google logo" className="google-logo" />
    Sign Up with Google
  </button>
));

// ── Main Component ────────────────────────────────────────────────────────────
const SignUp = ({ isMobile, ip }) => {
  const { login } = useAuth();

  const [showPass,     setShowPass]     = useState(false);
  const [showConfPass, setShowConfPass] = useState(false);
  const [googleUser,   setGoogleUser]   = useState(null);
  const [regNme,       setRegName]      = useState("");
  const [phone,        setPhone]        = useState("");
  const [email,        setEmail]        = useState("");
  const [pass,         setPass]         = useState("");
  const [confPass,     setConfPass]     = useState("");
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [isVerified,   setIsVerified]   = useState(false);
  const [loading,      setLoading]      = useState(false);

  const showToast = useCallback((msg) =>
    toast((t) => <AppToast id={t.id} message={msg} isMobile={isMobile} />), [isMobile]);

  const loginWithGoogle = useGoogleLogin({
    onSuccess: (codeResponse) => setGoogleUser(codeResponse),
    onError:   (error) => console.log("Login Failed:", error),
  });

  useEffect(() => {
    if (!googleUser) return;
    axios.get(`https://www.googleapis.com/oauth2/v1/userinfo?access_token=${googleUser.access_token}`, {
      headers: { Authorization:`Bearer ${googleUser.access_token}`, Accept:"application/json" },
    })
    .then((res) => SocialRegisterCall({ name:res.data.name, email:res.data.email, id:res.data.id }))
    .catch((err) => console.log(err));
  }, [googleUser]);

  const LoginCall = useCallback(async (payload) => {
    setLoading(true);
    try {
      const enc  = await apiEncryptRequest(payload);
      const res  = await instance.post("/login", enc);
      const data = await decryptData(res.data);
      login(data[0].user);
    } catch {
      showToast("Oops! Something went wrong. Please try again later or contact support.");
    }
    setLoading(false);
  }, [login, showToast]);

  const SocialRegisterCall = useCallback(async (data) => {
    if (!data) return;
    setLoading(true);
    const login_With = isPWA() ? "pwa" : "web";
    try {
      const enc = await apiEncryptRequest({ name:data.name, phone:"", ip, email:data.email, password:"", password_confirmation:"", login_by:"google", id:data.id, device_token:"", login_with:login_With, is_main:"1", parent_id:null });
      const res = await instance.post("/register", enc);
      const loginResponse = await decryptData(res.data);
      if (loginResponse[0].success) login(loginResponse[0].user);
    } catch {
      showToast("Oops! Something went wrong. Please try again later or contact support.");
    }
    setLoading(false);
  }, [ip, login, showToast]);

  const RegisterCall = useCallback(async () => {
    if (!isVerified)       { showToast("Validate Captcha");                            return; }
    if (!regNme)           { showToast("Please enter your Name");                      return; }
    if (!email)            { showToast("Please enter your Email");                     return; }
    if (!pass)             { showToast("Please enter Password");                       return; }
    if (!confPass)         { showToast("Please enter Confirm Password");               return; }
    if (pass !== confPass) { showToast("Password and Confirm Password do not match!"); return; }
    // Fix: original used || which is always true — !selectedCountry covers both null and ""
    if (!selectedCountry)  { showToast("Please Select your country");                  return; }

    setLoading(true);
    const login_With = isPWA() ? "pwa" : "web";
    try {
      const enc = await apiEncryptRequest({ name:regNme, phone, ip, email, password:pass, password_confirmation:confPass, login_by:"manual", is_main:"1", parent_id:null, country:selectedCountry });
      const res = await instance.post("/register", enc);
      const loginResponse = await decryptData(res.data);
      if (loginResponse[0]?.success) {
        await LoginCall({ email, password:pass, device_token:"", login_with:login_With });
      } else {
        showToast(loginResponse[0]?.message ?? "Registration failed. Please try again.");
      }
    } catch (error) {
      showToast(String(error));
    }
    setLoading(false);
  }, [isVerified, regNme, email, pass, confPass, selectedCountry, ip, phone, LoginCall, showToast]);

  return (
    <div>
      <style>{MOBILE_CSS}</style>
      <div className="apptoast-align">
        <Toaster position={isMobile ? "top-center" : "bottom-right"} reverseOrder={false} />
      </div>

      {isMobile ? (
        /* ── MOBILE — fluid card, no fixed widths ──────────────────────── */
        <div className="su-card">

          {/* Google SSO */}
          <button className="su-google-btn" onClick={loginWithGoogle}>
            <img src="/images/google_logo_icon.svg" alt="Google logo" />
            Sign Up with Google
          </button>
          <div className="su-or">or</div>

          {/* Name */}
          <div className="su-field">
            <img src="/images/user-white.svg" alt="" />
            <input className="su-input" type="text" placeholder="Name" value={regNme} onChange={(e) => setRegName(e.target.value)} />
          </div>

          {/* Phone */}
          <div className="su-field">
            <img src="/images/phone.svg" alt="" />
            <input className="su-input" type="tel" placeholder="Phone (optional)" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>

          {/* Email */}
          <div className="su-field">
            <img src="/images/email.svg" alt="" />
            <input className="su-input" type="email" placeholder="Email Address" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>

          {/* Password */}
          <div className="su-field">
            <img src="/images/lock.svg" alt="" />
            <input className="su-input" type={showPass ? "text" : "password"} placeholder="Password" value={pass} onChange={(e) => setPass(e.target.value)} />
            <EyeToggle show={showPass} onToggle={() => setShowPass((p) => !p)} />
          </div>

          {/* Confirm Password — own state, not shared with Password field */}
          <div className="su-field">
            <img src="/images/lock.svg" alt="" />
            <input className="su-input" type={showConfPass ? "text" : "password"} placeholder="Confirm Password" value={confPass} onChange={(e) => setConfPass(e.target.value)} />
            <EyeToggle show={showConfPass} onToggle={() => setShowConfPass((p) => !p)} />
          </div>

          {/* Country */}
          <div className="su-field">
            <img src="/images/location.png" alt="" />
            <CountrySelect onChange={setSelectedCountry} />
          </div>

          <p className="su-note">Note: All fields are mandatory</p>

          {/* reCAPTCHA — centred, scales on very narrow screens via CSS */}
          <div className="su-captcha">
            <ReCAPTCHA sitekey={RECAPTCHA_KEY} onChange={() => setIsVerified(true)} />
          </div>

          <button className="su-submit" onClick={RegisterCall}>Sign Up</button>

          <div className="su-already">
            <span>Already registered?</span>
            <a href="/login">Login</a>
          </div>
        </div>
      ) : (
        /* ── WEB (unchanged) ──────────────────────────────────────────── */
        <div style={{ height:"55rem" }}>
          <Row>
            <div className="col-md-4" />
            <div className="col-md-4">
              <div className="register-main see-full" style={{ marginTop:"8rem" }}>
                <div className="bg-img"><img src="/images/blue-box-bg.svg" alt="" /></div>
                <form className="see-full">
                  <input type="hidden" name="_token" value="IHks1cEdGGmsvouWsdVeWVHE29KFoaLV0iN8cPkE" />
                  <div className="btm mob">
                    <div className="google-act">
                      <a href="/auth/google">
                        <img src="/images/google_logo_icon.svg" alt="" />
                        <span>Sign Up with Google</span>
                      </a>
                    </div>
                    <div className="or">or</div>
                  </div>
                  <div className="form-top">
                    <div className="input-container">
                      <img src="/images/user-white.svg" alt="" className="imgIcon-signup" />
                      <input type="text" name="name" className="input-signup" placeholder="Name" value={regNme} onChange={(e) => setRegName(e.target.value)} />
                    </div>
                    <div className="input-container">
                      <img src="/images/email.svg" alt="" className="imgIcon-signup" />
                      <input type="text" name="email" className="input-signup" placeholder="Email Address" value={email} onChange={(e) => setEmail(e.target.value)} />
                    </div>
                    <div className="input-container" style={{ marginTop:"15px" }}>
                      <img src="/images/lock.svg" alt="" className="imgIcon-signup" />
                      <input type={showPass ? "text" : "password"} id="password-field" className="input-signup" name="password" placeholder="Password" value={pass} onChange={(e) => setPass(e.target.value)} />
                      <EyeToggle show={showPass} onToggle={() => setShowPass((p) => !p)} />
                    </div>
                    <div className="input-container" style={{ marginTop:"15px" }}>
                      <img src="/images/lock.svg" alt="" className="imgIcon-signup" />
                      <input type={showConfPass ? "text" : "password"} id="confirm-password-field" className="input-signup" name="confirm_password" placeholder="Confirm Password" value={confPass} onChange={(e) => setConfPass(e.target.value)} />
                      <EyeToggle show={showConfPass} onToggle={() => setShowConfPass((p) => !p)} />
                    </div>
                    <div className="input-container" style={{ marginTop:"15px" }}>
                      <img src="/images/location.png" alt="" className="imgIcon-signup" />
                      <CountrySelect onChange={setSelectedCountry} />
                    </div>
                    <span style={{ fontSize:"24px" }}>Note: All fields are mandatory</span>
                  </div>
                  <div style={{ display:"block", marginTop:"15px", marginBottom:"40px", marginLeft:"8rem" }}>
                    <ReCAPTCHA sitekey={RECAPTCHA_KEY} onChange={() => setIsVerified(true)} />
                  </div>
                  <div style={{ display:"grid", justifyItems:"center" }}>
                    <div className="log-in" onClick={RegisterCall}>
                      <a href="#" className="media-link">
                        <div className="media-banner" style={{ width:"auto", height:"50px", marginTop:"10px" }}>
                          <img className="normal-banner"    src="/images/signup-btn-bg.png" alt="" />
                          <img className="hover-img-banner" src="/images/search-btn-hover.png" alt="" />
                          <span className="login-text" style={{ fontSize:"24px", color:"#07528B", marginTop:"0px" }}>Sign Up</span>
                        </div>
                      </a>
                    </div>
                  </div>
                </form>
                <div className="btm desk">
                  <div className="or" style={{ fontSize:"24px" }}>or</div>
                  <GoogleButtonWeb onClick={loginWithGoogle} />
                  <span className="rgst mob" style={{ fontSize:"24px" }}>Already Registered?</span>
                  <a href="/login" style={{ fontSize:"24px" }}>Login</a>
                </div>
              </div>
            </div>
            <div className="col-md-4" />
          </Row>
        </div>
      )}

      {loading && (
        <div className="loading-overlay" style={{ zIndex:"9999999999999999" }}>
          <Loader isLoading={loading} />
        </div>
      )}
    </div>
  );
};

export default SignUp;